import { z } from 'zod';
import { createServiceHttpClient, noopLogger } from '@wit-support/core-ports';
import { RISK_MATCH_CONFIDENCE_ORDER, } from './types.js';
/**
 * HTTP-адаптер `RiskAlertMatcherPort` поверх поиска списаний платёжного
 * сервиса (`GET /v3/payment/internal/charges/search`). Вся проводка к чужому
 * контракту изолирована здесь: наружу уезжает только четыре исхода
 * `RiskAlertMatchResult`.
 *
 * ПРО СПИСАНИЕ РЕШАЕТ СЕРВЕР, ПРО ЧЕЛОВЕКА — МЫ. Граница проходит именно так,
 * и обе половины ниже сформулированы буквально.
 *
 * Половина сервера: с контракта 2026-08-25 у ответа есть поле `outcome`
 * (`auto` / `review` / `none`), и оно — ЕДИНСТВЕННОЕ основание утверждать, что
 * алерт указывает НА ЭТО КОНКРЕТНОЕ СПИСАНИЕ. `auto` значит «это та самая
 * транзакция, привязывайте без человека». Сервер ставит его только по ЧЕТЫРЁМ
 * откалиброванным путям (`autoLinkBasis`): `network-id` (идентификатор
 * транзакции сети глобально уникален — 171/171 на боевых алертах Visa),
 * `code+card` (код авторизации с восстановленными нулями плюс четыре цифры
 * карты — 0 ложных срабатываний на 200 000 фантомных алертов), `code+token`
 * (код + дата + сумма, а расхождение карты объяснено сетевым токеном) и, с
 * редакции спеки от 2026-08-27, `code+amount` (код авторизации + ТОЧНАЯ сумма
 * в окне сеттлмента [-4;+1] дня + обязательный `notAfter`; карта в этом пути
 * игнорируется НАМЕРЕННО — расхождение карты уже покрывает `code+token`, а
 * повторная сверка здесь лишь сузила бы путь, придуманный ловить то, что
 * карточные пути пропускают; не пробуется, если уже совпал `networkId` —
 * `network-id` доминирует над каскадом целиком, как и над остальными тремя;
 * всегда приходит при `confidence: high`). Все четыре считаются по ПОЛНОМУ
 * множеству кандидатов, которое видит только сервер; мы видим страницу. На
 * боевой сверке (см. числа ниже, снятой ДО редакции 2026-08-27 и потому не
 * знающей о `code+amount`) из 1592 ответов `auto` распределение путей такое:
 * `code+card` — 1375, `network-id` — 171, `code+token` — 46. Отдельный факт
 * про последний путь: все 46 приехали при `confidence: high`, то есть
 * `confirmed` НЕ является единственным уровнем, на котором сервер разрешает
 * привязку, и завязываться на него нельзя.
 *
 * Поэтому прежняя самодельная логика («единственный кандидат с совпавшим кодом
 * авторизации, при полной выдаче») выброшена целиком, и вернуть её нельзя даже
 * «на всякий случай»: она считала по одной отданной странице и объявляла
 * уникальным то, что уникально лишь в пределах `limit`. Отдельно: код
 * авторизации сам по себе основанием НЕ является — 11–40% случайных кодов
 * существуют в зеркале платёжного сервиса и без всякой связи с алертом.
 *
 * Половина наша: `single-customer` (см. ниже). Сервер отвечает `review`,
 * потому что не смог выбрать КАКОЕ ИМЕННО списание, — вопрос про списание он
 * оставляет открытым и никакого решения по нему мы за него не принимаем. Но
 * кейс привязывает ЧЕЛОВЕКА, и когда все кандидаты — один и тот же человек,
 * ответ про человека есть независимо от того, какое из его списаний имелось в
 * виду. Это единственное решение, которое осталось НАШИМ; оно обставлено
 * порогом уверенности (`>= medium`) и собственным условием полноты —
 * обоснование числами прямо у правила.
 *
 * ЧИСЛА, НА КОТОРЫЕ ССЫЛАЕТСЯ ВЕСЬ ФАЙЛ (замер 25.08.2026 — прогон запроса в
 * нынешнем его виде против боевого платёжного сервиса). На проде 1866 кейсов:
 * 1636 уже привязаны матчером, 230 ждут ручного разбора. Прогон по этим 230
 * дал `auto` — 142, `review` — 88, `none` — 0; из 88 ответов `review` ровно в
 * одного известного клиента схлопываются 49. Итого 142 + 49 = 191 кейс
 * привязывается автоматически, оператору остаётся 39. Сверка по 1636 уже
 * привязанным расхождений не дала ни одного: 1625 подтверждены точно, в 11 наш
 * клиент оказался среди нескольких кандидатов. Из этих же 1636 при переспросе
 * по новому контракту 1592 возвращаются с `outcome: auto` (их распределение по
 * путям — выше), а остальные 44 — с `review` (33 `medium`, 6 `high`, 3 `low`,
 * 2 `confirmed`); 1592 + 44 = 1636.
 *
 * `unavailable` и `not_found` НИКОГДА не схлопываются: первое значит «спросить
 * не удалось», второе — «искали, в биллинге такого нет». Апстрим держит ту же
 * границу: «никого не нашли» — это 200 с пустым `items`, а 404 означает «роут
 * не выкачен».
 *
 * Случай «база не задана» этот файл НЕ обслуживает: тогда адаптер вообще не
 * конструируется, а вызывающий подставляет `createUnconfiguredMatcher()` из
 * `ports.ts` — ровно тот же приём, что `createNoopIdentityResolver` при пустом
 * `IDENTITY_API_BASE_URL`.
 */
/** Путь эндпоинта. Единственное место, где он назван. */
const SEARCH_PATH = '/v3/payment/internal/charges/search';
/**
 * Просим МАКСИМУМ (эндпоинт принимает 1..100, по умолчанию 20).
 *
 * На решение сервера размер страницы не влияет — он считает по своему полному
 * множеству. Но неоднозначный кейс разбирает человек ГЛАЗАМИ, и обрезанный до
 * двадцати список кандидатов означал бы, что нужного в нём может просто не
 * быть. Полнота страницы к тому же — предусловие нашего `single-customer`,
 * и там же эта константа работает вторым поясом: страница, набитая ровно под
 * лимит, подозрительна сама по себе — ветка карты у апстрима доходит до 1 073
 * списаний, то есть заведомо больше страницы.
 */
const SEARCH_LIMIT = 100;
/**
 * Платёжный сервис попросил притормозить. ОТДЕЛЬНЫЙ класс, а не общий
 * `Error`: бэкфилл на 5 000 алертов упирается в лимит именно так, и разница
 * между «сломалось, ретраить сразу» и «нас ограничили, ретраить не раньше чем
 * через N» — это разница между восстановлением и штормом, который сам себя
 * поддерживает. `retryAfterMs` — разобранный заголовок `Retry-After` (секунды
 * или HTTP-дата), `null` — если апстрим его не прислал.
 */
export class RiskMatcherRateLimitedError extends Error {
    retryAfterMs;
    constructor(retryAfterMs) {
        super(`risk-matcher: HTTP 429${retryAfterMs != null ? ` (retry after ${retryAfterMs} ms)` : ''}`);
        this.name = 'RiskMatcherRateLimitedError';
        this.retryAfterMs = retryAfterMs;
    }
}
/** `Retry-After`: либо секунды, либо HTTP-дата (RFC 9110). Понимаем оба. */
const parseRetryAfter = (raw) => {
    if (!raw)
        return null;
    const trimmed = raw.trim();
    if (trimmed === '')
        return null;
    const seconds = Number(trimmed);
    if (Number.isFinite(seconds) && seconds >= 0)
        return Math.round(seconds * 1000);
    const at = Date.parse(trimmed);
    if (Number.isNaN(at))
        return null;
    return Math.max(0, at - Date.now());
};
// ---------------------------------------------------------------------------
// Запрос
// ---------------------------------------------------------------------------
const clean = (v) => {
    const t = (v ?? '').trim();
    return t === '' ? null : t;
};
// Валидация ПЕРЕД отправкой — ровно та, что заявлена у эндпоинта. Смысл не в
// вежливости: непрошедшее значение апстрим отвергает 400-ым, а 400 здесь
// трактуется как дефект нашего маппинга и бросается. Мусор в атрибутах алерта
// (маскированный «*4242», код авторизации с пробелами, идентификатор сети с
// дефисами) — обычное дело, и он не должен превращать идентификацию в ошибку.
// Негодное значение просто не участвует в поиске.
const NETWORK_ID_RE = /^[A-Za-z0-9]{8,40}$/;
const AUTH_CODE_RE = /^[A-Za-z0-9-]{1,32}$/;
const LAST4_RE = /^\d{4}$/;
const CURRENCY_RE = /^[A-Za-z]{3}$/;
/**
 * Короче четырёх знаков код авторизации не бывает — это заглушка эмитента.
 * Сервер такой код ИГНОРИРУЕТ (`query.authCodeIgnored: "too-short"`) и без
 * других якорей отвечает 400 `AUTH_CODE_TOO_SHORT`.
 *
 * Порог продублирован здесь ТОЛЬКО чтобы не платить за заведомо провальный
 * вызов: на разовом бэкфилле фантомных алертов таких сотни. Отправлять код мы
 * при этом не перестаём — если якорь есть и без него, он уезжает как есть, и
 * расхождение с серверным порогом ловится обработчиком `AUTH_CODE_TOO_SHORT`
 * ниже, а не тишиной. Своей логики на `query.authCodeIgnored` тоже не нужно:
 * проигнорированный код не попадёт в `matchedBy` позиции, а именно оттуда
 * берётся всё, что мы утверждаем в `basis`.
 */
const AUTH_CODE_MIN_ANCHOR_LENGTH = 4;
/**
 * Потолок длины бренда карты. НЕ МАСКА ФОРМАТА и не попытка угадать список
 * написаний: контракт задаёт для поля просто `string`, а какие написания сервер
 * умеет нормализовать, знает только он — своя маска молча выбрасывала бы
 * работающее значение (ровно та ошибка, которой мы избежали с кодом
 * авторизации).
 *
 * Это защита от МУСОРА: у известных написаний длина 4–16 знаков (`Visa`,
 * `Discover Card`, `American Express`), 32 даёт двукратный запас на любое
 * разумное новое написание, но отсекает случай, когда в поле бренда приехал
 * абзац текста и уехал бы в URL и в логи. Значение сверх потолка не
 * отправляется — фильтр не применится, ответ останется корректным.
 */
const CARD_BRAND_MAX_LENGTH = 32;
/**
 * Сети, чьи алерты — ФРОД-РЕПОРТЫ: Ethoca (раннее предупреждение Mastercard),
 * TC40 и SAFE (фрод-фиды Visa и Mastercard).
 *
 * Такой алерт регулярно ссылается на авторизацию, записанную у нас как
 * `failed`: код авторизации у попытки есть, а списания не случилось. Без
 * `includeNonSucceeded=true` транзакция не находится вовсе (доказано боевыми
 * кейсами), поэтому по этим сетям поиск расширяется.
 */
export const FRAUD_FEED_NETWORKS = ['Ethoca', 'TC40', 'SAFE'];
/**
 * Надо ли расширять поиск на несостоявшиеся списания.
 *
 * Правило смотрит на ДВА признака кейса, и второй — не перестраховка. Наш
 * собственный `deriveKind` переписывает алерт информационной сети в
 * `kind: 'info'` (TC40 и SAFE — телеметрия, работы по ним нет), поэтому
 * фрод-репорт TC40 приезжает сюда БЕЗ `kind: 'fraud'`: правило только по
 * `kind` пропустило бы ровно ту сеть, ради которой флаг и нужен. А правило
 * только по сети пропустило бы фрод-алерт сети, которую адаптер не узнал
 * (`network: null`) или которая появится позже.
 *
 * Считается по ВСЕМУ кейсу: кейс — один инцидент из нескольких алертов разных
 * сетей, искать нужно одну и ту же транзакцию, и хватает одного фрод-алерта.
 */
export const needsNonSucceededCharges = (q) => q.alertKinds.includes('fraud') || q.alertNetworks.some((n) => FRAUD_FEED_NETWORKS.includes(n));
/** UTC-день в формате `YYYY-MM-DD`. Локальная зона не участвует намеренно. */
const utcDay = (d) => {
    if (d == null || Number.isNaN(d.getTime()))
        return null;
    return d.toISOString().slice(0, 10);
};
/** Момент в ISO-8601; `Invalid Date` даёт `null`, а не строку «Invalid Date». */
const utcInstant = (d) => {
    if (d == null || Number.isNaN(d.getTime()))
        return null;
    return d.toISOString();
};
/**
 * Сигналы совпадения в КАНОНИЧЕСКОМ порядке — от самого доказательного к
 * самому слабому. Порядок фиксирован, чтобы `basis` в аудите и строка «Совпало
 * по» у кандидата не зависели от того, в каком порядке апстрим перечислил
 * поля.
 *
 * `networkId` первый не по алфавиту: идентификатор транзакции сети — не ещё
 * один атрибут, а глобально уникальный ключ самой транзакции. Всё остальное
 * лишь сужает круг.
 */
const SIGNAL_ORDER = ['networkId', 'authCode', 'last4', 'date', 'amount'];
/**
 * Сигналы, СУЖАЮЩИЕ поиск до горстки списаний. По замерам апстрима ветка кода
 * авторизации даёт максимум 5 списаний, ветка карты — 1 073, и ни одна не
 * доходит до предохранителя просмотра (2 000 на признак). Дата и сумма
 * селективными не являются: суточная ветка легко даёт две тысячи кандидатов и
 * обрезается — «единственный человек» в ней означает лишь «единственный в
 * просмотренном куске суток». См. условие полноты для пути `single-customer`.
 */
const SELECTIVE_SIGNALS = ['networkId', 'authCode', 'last4'];
/**
 * Ниже этого уровня уверенности апстрима путь `single-customer` не работает.
 * Значение не наше: `medium` документация определяет ровно как наш случай
 * («все кандидаты лучшего уровня принадлежат одному клиенту»), а `low`
 * включает голый код авторизации, которого недостаточно. Сравнивается с
 * УРОВНЕВОЙ уверенностью ответа (`data.confidence`), а не с максимумом по
 * позициям, — подробности у самого правила.
 */
const MIN_SINGLE_CUSTOMER_CONFIDENCE = 'medium';
const orderSignals = (signals) => {
    const uniq = [...new Set(signals)];
    const known = SIGNAL_ORDER.filter((s) => uniq.includes(s));
    // Незнакомый сигнал НЕ выбрасываем (апстрим вправе добавить свой), но кладём
    // после известных и сортируем — иначе порядок недетерминирован.
    const unknown = uniq.filter((s) => !SIGNAL_ORDER.includes(s)).sort();
    return [...known, ...unknown];
};
/**
 * Собирает query-строку из того, что эндпоинт УМЕЕТ принимать.
 *
 * `bin`, `arn`, `externalOrder`, `descriptor`, `mid`, `customerEmail`,
 * `processor` в `RiskAlertMatchQuery` есть, но поиск по ним не поддержан — они
 * не отправляются и не участвуют в решении.
 *
 * СТРАНА АЛЕРТА — ОТДЕЛЬНЫЙ СЛУЧАЙ, и стоит она здесь не рядом с `arn` и `mid`.
 * Её не «пока не поддержали» — её СОЗНАТЕЛЬНО ОТВЕРГЛИ по замеру, и в типе
 * запроса поля для неё нет намеренно. На 1739 подтверждённых боевых матчах
 * страна алерта расходится со страной эмитента карты в 25% случаев, а на
 * матчах по глобально уникальному идентификатору транзакции — всё ещё в 7,6%
 * (типичные пары `US→BR`, `ES→US`, `RU→MX`). Как фильтр она отрезала бы
 * четверть верных матчей, как сигнал — шум. Добавлять её «как очевидное
 * улучшение» нельзя: это ухудшение с известной ценой.
 *
 * `null` = искать нечем. Якорем считается `networkId`, `authCode` (не короче
 * четырёх знаков), `last4` или `date`: без них апстрим отвечает 400
 * `SEARCH_CRITERIA_REQUIRED`. Сумма якорем НЕ является — она лишь уточняет
 * уже заданный поиск, и `notAfter` тоже: он только отсекает лишнее.
 *
 * ИДЕНТИФИКАТОР ТРАНЗАКЦИИ СЕТИ — САМОСТОЯТЕЛЬНЫЙ ЯКОРЬ. Для Visa он
 * глобально уникален, и на боевых данных 171 алерт из 171 нашёлся ровно в
 * одно списание. Запрос ради одного только `networkId` поэтому имеет смысл
 * делать даже когда ни кода, ни карты, ни даты у алерта нет: это самый
 * доказательный ключ, который у нас вообще бывает.
 *
 * Нынешнюю очередь он при этом НЕ разберёт, и обещать этого не надо: из 230
 * неразобранных кейсов прода лишь 5 несут хоть какой-то `networkId` и ни один
 * — 15-значный Visa. Он работает на алерты, которые придут дальше.
 *
 * КОД АВТОРИЗАЦИИ УХОДИТ КАК ЕСТЬ. Ведущие нули провайдеры обрезают (`26044`
 * = `026044`, 25.2% кодов в зеркале платёжного сервиса начинаются с нуля), и
 * восстанавливает их СЕРВЕР — он же перебирает регистр. Достроить код самим
 * значит подменить улику: какой вариант в итоге совпал, видно в
 * `items[].authCodeMatch` (`exact` / `zero-padded`). Единственное, что мы с
 * ним делаем, — срезаем окружающие пробелы, и это разбор строки, а не правка
 * кода. Ровно то же и с `networkId`.
 *
 * БРЕНД КАРТЫ — ЖЁСТКОЕ ИСКЛЮЧЕНИЕ, А НЕ ПРИЗНАК СОВПАДЕНИЯ. Это главное, что
 * можно понять неправильно, и из этого следует всё остальное его поведение.
 * Сервер не «подмешивает бренд в скоринг» — он ВЫБРАСЫВАЕТ из рассмотрения
 * списания с другим ИЗВЕСТНЫМ брендом, причём на уровне запроса к базе, до
 * своего предохранителя просмотра: чужой бренд поэтому не может выесть кап и
 * спрятать за ним подходящую более старую транзакцию. Отсюда три следствия,
 * каждое из которых легко нарушить по невнимательности:
 *  — бренда НЕТ в `askedSignals`: список описывает, по чему мы искали
 *    СОВПАДЕНИЕ, и попади бренд туда, он бы всплыл в основании привязки как
 *    доказательство, которым не является;
 *  — бренда НЕТ и не будет в `matchedBy` ответа — по той же причине, только со
 *    стороны сервера;
 *  — бренд НЕ ЯКОРЬ. Запрос из одного только бренда бессмыслен (это «покажи
 *    все мастеркарды»), поэтому условие «нужен хотя бы один из `networkId` /
 *    `authCode` / `last4` / `date`» он не удовлетворяет.
 *
 * Уезжает КАК ПРИШЛО. Нормализацию написаний (`Discover Card` → `discover`)
 * держит сервер, он же отдаёт понятое значение эхом в `query.cardBrand`, а
 * нераспознанное написание просто игнорирует — фильтр тогда не применяется, и
 * ответ остаётся корректным, лишь менее узким. Собственную маску формата мы не
 * ставим намеренно: контракт задаёт для поля просто `string`, какие написания
 * сервер умеет нормализовать — знает только он, и лишняя строгость молча
 * выбросила бы работающее значение. Проверяем ровно две вещи — что значение не
 * пустое и что длина в разумных пределах (см. `CARD_BRAND_MAX_LENGTH`).
 *
 * НЕРАСПОЗНАННОЕ НАПИСАНИЕ ДЕГРАДИРУЕТ МОЛЧА, и это ОСОЗНАННЫЙ выбор, а не
 * недосмотр — вот причины, чтобы следующий читатель не чинил несуществующее и
 * знал, когда чинить настоящее.
 *  — Ответ остаётся ВЕРНЫМ. Фильтр только сужает; без него кандидатов больше, а
 *    не «не те». Цена молчания здесь — оператор разобрал руками то, что мог
 *    разобрать матчер, а не чужой биллинг в карточке клиента. Это принципиально
 *    иной класс, чем расхождение `outcome`/`autoLinkSafe`, которое видно
 *    снаружи через `reason`: там молчание стоило бы неверной привязки.
 *  — Отказ ДЕТЕРМИНИРОВАН по написанию: одно и то же написание либо всегда
 *    понимается сервером, либо всегда нет. Значит он ловится ОДНИМ прогоном
 *    очереди — тем самым, которым померены 13 кейсов, — а не требует наблюдения
 *    в реальном времени. Рецепт обнаружения: отправили непустой бренд, а в эхе
 *    ответа `query.cardBrand: null`.
 *  — В `reason` его класть нельзя: то поле объясняет причину НЕОДНОЗНАЧНОСТИ, а
 *    «бренд не понят» — свойство ЗАПРОСА, одинаково возможное при `matched`,
 *    `not_found` и `ambiguous`. Свалить туда — размыть поле до «разное».
 * Когда это перестанет быть верным и понадобится настоящий канал (отдельный
 * диагностический sink в опциях адаптера или поле «что сервер понял из
 * запроса» в результате): если источников алертов станет больше одного или
 * если написание бренда начнёт приходить из свободного поля, а не из
 * фиксированного словаря провайдера. До тех пор эхо-поле `query.cardBrand`
 * намеренно даже не разбирается — читать его, чтобы никуда не отдать, было бы
 * имитацией наблюдаемости.
 *
 * Что это даёт: на боевом прогоне всех 39 оставшихся неопознанных кейсов бренд
 * добавляет 13 разобранных и не теряет ни одного. Все 13 — Mastercard, у
 * которых в выдаче мешались чужие Visa с теми же четырьмя цифрами: кандидатов
 * становится меньше, лишние клиенты уходят, `confidence` поднимается с `low` до
 * `medium` — то есть срабатывает наш путь `single-customer` (№2865: 2
 * кандидата → 1; №2008: 3 → 1; №2067: 6 → 3, и все три оказались одним
 * клиентом). Ручная очередь 39 → 26, идентификация 97,9% → 98,6%.
 */
const buildCriteria = (q) => {
    const rawNetworkId = clean(q.networkId);
    const networkId = rawNetworkId != null && NETWORK_ID_RE.test(rawNetworkId) ? rawNetworkId : null;
    const rawAuthCode = clean(q.authCode);
    const authCode = rawAuthCode != null && AUTH_CODE_RE.test(rawAuthCode) ? rawAuthCode : null;
    const rawLast4 = clean(q.cardLast4);
    const last4 = rawLast4 != null && LAST4_RE.test(rawLast4) ? rawLast4 : null;
    const date = utcDay(q.transactionAt);
    // Заглушка эмитента якорем не работает — см. AUTH_CODE_MIN_ANCHOR_LENGTH.
    const authCodeIsAnchor = authCode != null && authCode.length >= AUTH_CODE_MIN_ANCHOR_LENGTH;
    if (networkId == null && !authCodeIsAnchor && last4 == null && date == null)
        return null;
    const params = new URLSearchParams();
    const askedSignals = [];
    if (networkId != null) {
        params.set('networkId', networkId);
        askedSignals.push('networkId');
    }
    if (authCode != null) {
        params.set('authCode', authCode);
        askedSignals.push('authCode');
    }
    if (last4 != null) {
        params.set('last4', last4);
        askedSignals.push('last4');
    }
    // БРЕНД — ИСКЛЮЧЕНИЕ, А НЕ ПРИЗНАК: `askedSignals.push` здесь намеренно НЕТ
    // (см. докблок). Уезжает как пришёл — регистр и внутренние пробелы наши, а
    // нормализация написаний серверная.
    const cardBrand = clean(q.cardBrand);
    if (cardBrand != null && cardBrand.length <= CARD_BRAND_MAX_LENGTH) {
        params.set('cardBrand', cardBrand);
    }
    if (date != null) {
        params.set('date', date);
        askedSignals.push('date');
    }
    // Суммы СТРОГО в минимальных единицах: `22.80` апстрим отвергает, а не
    // округляет. Дробное значение здесь означает, что источник дал доллары —
    // молча превращать их в центы нельзя, так что сумма просто не уточняет поиск.
    const amountCents = q.amountCents;
    if (amountCents != null && Number.isInteger(amountCents) && amountCents >= 0) {
        params.set('amountCents', String(amountCents));
        askedSignals.push('amount');
        // `currency` эндпоинт принимает ТОЛЬКО вместе с `amountCents`
        // (`CURRENCY_WITHOUT_AMOUNT`), поэтому она внутри этой же ветки. Это
        // валюта КАРТЫ — та, в которой провайдер алерта назвал сумму; пересчёт на
        // нашей стороне не делается ни здесь, ни где-либо ещё: сравнивает сервер,
        // и он же объясняет расхождение в `items[].amountMatch`.
        const currency = clean(q.currency);
        if (currency != null && CURRENCY_RE.test(currency)) {
            params.set('currency', currency.toUpperCase());
        }
    }
    // Момент создания алерта. Списания, созданные ПОЗЖЕ него, сервер исключает:
    // алерт не может ссылаться на будущую транзакцию. На живых данных правило
    // уже сняло коллизию, где «кандидат» был создан через месяц после алерта.
    const notAfter = utcInstant(q.alertCreatedAt);
    if (notAfter != null)
        params.set('notAfter', notAfter);
    // Флаг ставится только когда он нужен: расширять поиск на несостоявшиеся
    // списания по диспуту значит добавить в выдачу чужие попытки и своими
    // руками сделать однозначный ответ неоднозначным.
    if (needsNonSucceededCharges(q))
        params.set('includeNonSucceeded', 'true');
    // `dayTolerance` не отправляется: серверное умолчание (±1 день) — ровно то,
    // что нужно, а расчётная дата у провайдера алерта регулярно отличается от
    // даты списания на сутки.
    params.set('limit', String(SEARCH_LIMIT));
    return { params, askedSignals };
};
/**
 * КАНОНИЧЕСКИЙ КЛЮЧ ЗАПРОСА — ровно то, что уедет в query-строку, и ничего
 * сверх того. `null` = искать нечем (тот же случай, что `buildCriteria == null`:
 * адаптер отвечает `unavailable/empty_query`, не делая HTTP-вызова).
 *
 * Нужен снаружи ровно для одного: решить, отличается ли новая попытка
 * идентификации от предыдущей. `sameQuery` в
 * `apps/worker/src/handlers/risk-alert-identifier.ts` экономит на этом платный
 * вызов матчера (бэкфилл на 5 000 алертов упирается в 429 именно так), и
 * сравнивать там СЫРЫЕ поля `RiskAlertMatchQuery` — неправда: `source`,
 * `bin`, `arn`, `externalOrder`, `descriptor`, `mid`, `processor`,
 * `customerEmail` не уезжают никогда, `transactionAt` режется до `YYYY-MM-DD`,
 * `alertKinds`/`alertNetworks` схлопываются в один флаг `includeNonSucceeded`,
 * а негодные значения отбрасывает валидация выше. Второй алерт об ОДНОЙ
 * транзакции (у Ethoca есть ARN, у CDRN — идентификатор сети) даёт побайтово
 * тот же URL, и переспрашивать по нему не за что.
 *
 * Ключ живёт ЗДЕСЬ, а не копией в воркере, именно потому, что вторая копия
 * списка «что уезжает в URL» разъедется при первой же правке `buildCriteria` —
 * и разъедется ТИХО: правило начнёт экономить на изменившемся запросе, а в
 * логах это неотличимо от нормальной работы.
 *
 * ДЕТЕРМИНИЗМ — ОБЕЩАНИЕ ЭТОЙ ФУНКЦИИ, а не свойство реализации: одинаковый
 * вход даёт побайтово одинаковый ключ. Сегодня он выполняется сам собой
 * (`URLSearchParams` хранит порядок вставки, а порядок вставки в
 * `buildCriteria` задан кодом); если ключ когда-нибудь начнут собирать иначе,
 * обещание обязано пережить перестройку — вплоть до явной сортировки.
 *
 * `limit` внутри ключа безвреден: это константа, она одинакова у любых двух
 * запросов и сравнение не искажает.
 */
export const matchQuerySearchKey = (q) => buildCriteria(q)?.params.toString() ?? null;
// ---------------------------------------------------------------------------
// Ответ
// ---------------------------------------------------------------------------
/**
 * Поля, которые нужны ТОЛЬКО для показа оператору, разбираются терпимо
 * (`.catch(null)`): негодный тип в одном из них делает это поле пустым, но не
 * выбрасывает всю позицию. Строгость тут вредна — выброшенная позиция теряет
 * кандидата, а из-за чего именно она выброшена, оператор не узнает никогда.
 *
 * ГРАНИЦА ПРОВЕДЕНА ПО УЧАСТИЮ В РЕШЕНИИ О ПРИВЯЗКЕ, а не по «важности на
 * вид». Строго разбираются только `internalUserId`, `stripeCustomerId`,
 * `userEmail` и `matchedBy`: первые три образуют ключ схлопывания в человека,
 * последнее — условие полноты и текст основания. Всё остальное — имена, почты
 * для показа, карта целиком — в решении не участвует, и «правдоподобная» смена
 * типа (`"last4": 4242` числом вместо строки) не должна стоить оператору целого
 * кандидата, о пропаже которого он к тому же никак не узнает.
 *
 * `chargeId` — промежуточный случай, и названо это прямо: в решении о привязке
 * он не участвует, но служит ПОСЛЕДНЕЙ ступенью ключа схлопывания, то есть
 * влияет на ЧИСЛО кандидатов у безымянных позиций. Терпимость и здесь
 * безопаснее строгости: испорченный `chargeId` даёт `null` и уводит позицию на
 * ступень «номер строки», где она остаётся отдельным кандидатом, — тогда как
 * строгий разбор выбросил бы её целиком.
 *
 * С контракта 2026-08-27 это больше не значит «поле безобидно»: на пути `auto`
 * `chargeId` единственной позиции — ПОСЛЕДНИЙ резерв для `matchedChargeId`
 * (см. довод у `RiskAlertMatchResult.matchedChargeId` в `ports.ts`), а он
 * таргетирует РЕАЛЬНЫЙ возврат денег. Схема при этом ОСТАЁТСЯ терпимой — она
 * разбирает позиции ОБЕИХ веток (`auto` и `review`/`ambiguous`), и в
 * последних `chargeId` по-прежнему только показ и группировка, строгость там
 * была бы неверна по причинам выше. Строгость вместо этого стоит НЕ в схеме,
 * а В ТОЧКЕ РЕШЕНИЯ (`match()`, ветка `auto`): если ни конверт
 * (`data.matchedChargeId`), ни `single.chargeId` не назвали списание, это
 * трактуется как то же контрактное нарушение, что и рассогласование
 * `outcome`/`autoLinkSafe`, — кейс падает в `ambiguous` с
 * `reason: 'contract_mismatch'`, а не привязывает клиента к списанию, чей id
 * нам неизвестен.
 */
const lenient = (schema) => schema.nullish().catch(null);
const ConfidenceWire = z.enum(RISK_MATCH_CONFIDENCE_ORDER);
/**
 * Разбор `amountMatch` — ПЕРЕЧИСЛЕНИЕМ, а не свободной строкой.
 *
 * Незнакомый `kind` обнуляет весь объект (`lenient` ниже), и это осознанно:
 * подставить вместо него `mismatch` значило бы утверждать факт, которого
 * апстрим не говорил, — «суммы разные» вместо «мы не поняли ответ». Оператор
 * увидит отсутствие объяснения и посмотрит сам, а не поверит выдумке.
 */
const AmountMatchWire = z.object({
    kind: z.enum(['exact', 'fee-markup', 'fx-plausible', 'unknown-currency', 'mismatch']),
    impliedRate: lenient(z.number()),
    markupPct: lenient(z.number()),
});
const ChargeWire = z.object({
    chargeId: lenient(z.string()),
    // Появилось в редакции 2026-08-27, показ-только (см. докблок
    // `RiskCandidateCharge.paymentIntentId`): решение о привязке и о возврате
    // по-прежнему принимается по `chargeId`, терпимость здесь той же природы.
    paymentIntentId: lenient(z.string()),
    invoiceId: lenient(z.string()),
    /** Дата САМОГО списания у провайдера, а не время нашей загрузки. */
    createdAt: lenient(z.string()),
    /**
     * `succeeded` / `pending` / `failed` — как назвал апстрим, перечислением не
     * ограничиваем. Ровно та разница, ради которой существует
     * `includeNonSucceeded`: боевой ответ вернул два кандидата на одни четыре
     * цифры — неудавшуюся попытку и состоявшееся списание, — и первой в выдаче
     * стояла именно неудавшаяся.
     */
    status: lenient(z.string()),
    amount: lenient(z.number()),
    currency: lenient(z.string()),
    /** У Stripe по этому списанию уже есть диспут — сильнейший признак для выбора. */
    disputed: lenient(z.boolean()),
    refunded: lenient(z.boolean()),
    amountRefunded: lenient(z.number()),
    // ТРИ СТРОГИХ ПОЛЯ — ключ схлопывания в человека (см. `groupByCustomer`).
    // Здесь испорченный тип менял бы не показ, а состав кандидатов, и терпимость
    // означала бы «склеили не тех» вместо «не показали поле».
    internalUserId: z.string().nullish(),
    userEmail: z.string().nullish(),
    stripeCustomerId: z.string().nullish(),
    stripeEmail: lenient(z.string()),
    billingEmail: lenient(z.string()),
    billingName: lenient(z.string()),
    // Карта разбирается терпимо ЦЕЛИКОМ, а не только по полям: `card: "visa"`
    // строкой вместо объекта выбрасывало бы позицию из выдачи — кандидат исчезал
    // бы у оператора, а при одном клиенте ещё и рушил полноту (`dropped > 0`) и
    // гасил `single-customer`. Ни одно поле карты в решении не участвует, значит
    // и контейнер строгим быть не может: граница проведена по участию в решении,
    // и контейнер обязан следовать за содержимым.
    card: lenient(z
        .object({
        brand: lenient(z.string()),
        last4: lenient(z.string()),
        funding: lenient(z.string()),
        country: lenient(z.string()),
        walletType: lenient(z.string()),
        walletLast4: lenient(z.string()),
        /**
         * Списание прошло по СЕТЕВОМУ ТОКЕНУ: Stripe видел цифры токена, а
         * эмитент в алерте — цифры настоящей карты. Единственный факт, который
         * объясняет верное совпадение с «неправильным» `last4`; без него
         * оператор отвергает правильного кандидата за то, что тот выглядит
         * неправильно.
         */
        networkTokenUsed: lenient(z.boolean()),
        networkTransactionId: lenient(z.string()),
    })
        .passthrough()),
    // СТРОГО: `matchedBy` — условие полноты (селективный якорь у каждой позиции)
    // и текст основания. Испорченный тип здесь менял бы решение, а не показ.
    matchedBy: z.array(z.string()).nullish(),
    // Перечисление, а не строка: `near` — это «совпал СОСЕДНИЙ день», и выдать
    // незнакомое значение за `exact` было бы прямой неправдой в карточке.
    dateMatch: lenient(z.enum(['exact', 'near'])),
    authCodeMatch: lenient(z.enum(['exact', 'zero-padded'])),
    amountMatch: lenient(AmountMatchWire),
    confidence: lenient(ConfidenceWire),
});
/**
 * Конверт разбираем СТРОГО в части `data.items` (без него ответ бессмыслен), а
 * поля решения — терпимо: отсутствующее или испорченное становится `null` и не
 * роняет разбор. `null` при этом СТРОЖЕ любого значения, а не «худший случай из
 * перечисления»: ни один путь ниже на `null` не срабатывает — путь сервера
 * требует ровно `'auto'`, наш — ровно `'review'`, а условие полноты требует
 * пришедшего `total`. Право на привязку обязано ПОДТВЕРЖДАТЬСЯ, а не
 * предполагаться, и «поле не пришло» — не подтверждение.
 *
 * `total` и `truncated` разбираются терпимо по той же причине, что и соседи:
 * строка `"1400"` вместо числа уронила бы `SearchEnvelope.parse` исключением, а
 * исключение здесь означает бесконечный ретрай BullMQ на ответе, который
 * повторится точно таким же, — то есть платный вызов за платным вызовом вплоть
 * до 429. Для `total` терпимость безопасна ровно потому, что испорченное
 * значение даёт `null`, а `null` ведёт в `ambiguous`; `truncated` не читает
 * вообще ни одна ветка решения (все его упоминания в файле — комментарии), и
 * ронять из-за него ответ целиком было бы границей, проведённой наоборот.
 *
 * А вот `items` — БЕЗ `.default([])`, и это не мелочь. Значение по умолчанию
 * срабатывает на ОТСУТСТВУЮЩЕМ ключе, то есть ответ, где массив переименован,
 * вырезан шлюзом или отдан другой веткой роутера, читался бы как «искали,
 * никого нет» и записывал бы кейсу терминальное `not_found` — красную пилюлю
 * оператору по КАЖДОМУ кейсу, хотя не искал никто. «Никого не нашли» апстрим
 * отдаёт `items: []`, а пропажа самого массива — расхождение более грубое, чем
 * «не разобралась ни одна из N позиций» ниже, которое честно бросает. Значит и
 * трактоваться она обязана не мягче: пусть падает.
 */
const SearchEnvelope = z.object({
    data: z.object({
        items: z.array(z.unknown()),
        /** Единственное поле решения. `autoLinkSafe` — его строгий синоним. */
        outcome: lenient(z.enum(['auto', 'review', 'none'])),
        autoLinkSafe: lenient(z.boolean()),
        autoLinkBasis: lenient(z.enum(['network-id', 'code+card', 'code+token', 'code+amount'])),
        confidence: lenient(ConfidenceWire),
        truncated: lenient(z.boolean()),
        total: lenient(z.number()),
        /**
         * Списание, которое сервер НАЗВАЛ по имени, — не «схлопнутый в человека»
         * путь `single-customer`, а конкретное `charges[].chargeId`. Разбирается
         * терпимо, как всё остальное поле решения: отсутствие (старый апстрим,
         * контракт до 2026-08-25) или испорченный тип не должны ронять разбор
         * ответа целиком, а просто не дают наружу конкретного списания.
         */
        matchedChargeId: lenient(z.string()),
    }),
});
/** Тело 400: ветвимся по `code`, а не по человекочитаемому `message`. */
const SearchErrorWire = z.object({ code: z.string().nullish() });
/**
 * Коды 400, детерминированные ПО ЗНАЧЕНИЮ: апстрим отверг конкретное поле
 * запроса по правилу собственной валидации, и повтор с тем же значением даст
 * тот же 400 — ретраить бессмысленно, но и ронять кейс исключением не за что
 * (см. довод у ветки `res.status === 400`). `SEARCH_CRITERIA_REQUIRED` в этот
 * список НЕ входит — у него уже есть свой исход (`empty_query`), совпадающий
 * с нашей же проверкой якорей до сетевого вызова.
 */
const DETERMINISTIC_400_CODES = new Set([
    'AUTH_CODE_TOO_SHORT',
    'INVALID_NETWORK_ID',
    'INVALID_AUTH_CODE',
    'INVALID_LAST4',
    'INVALID_DATE',
    'INVALID_AMOUNT',
    'INVALID_CURRENCY',
    'CURRENCY_WITHOUT_AMOUNT',
    'INVALID_SEARCH_PARAM',
]);
const firstNonEmpty = (...vals) => {
    for (const v of vals) {
        const c = clean(v);
        if (c != null)
            return c;
    }
    return null;
};
/**
 * Уверенность позиции: своя, иначе уровня ответа.
 *
 * Апстрим вправе прислать `confidence` один раз на весь ответ — тогда она
 * относится к каждой позиции, и не размножить её значило бы потерять ровно то
 * поле, по которому оператор и выбирает кандидата. Своя уверенность позиции
 * при этом СИЛЬНЕЕ: она конкретнее.
 */
const resolveConfidence = (c, envelope) => c.confidence ?? envelope;
/** Место уверенности в шкале; `null` слабее любого известного значения. */
const confidenceRank = (c) => c == null ? -1 : RISK_MATCH_CONFIDENCE_ORDER.indexOf(c);
/**
 * Сильнейшая уверенность среди списаний человека — величина для ПОКАЗА
 * кандидата: он стоит столько, сколько его лучшее совпадение. Порогом
 * `single-customer` она не является намеренно (см. правило): на том пути группа
 * ровно одна, и максимум по её списаниям вырождается в максимум по всему
 * ответу.
 */
const strongestConfidence = (charges) => charges.reduce((best, c) => (confidenceRank(c.confidence) > confidenceRank(best) ? c.confidence : best), null);
/** Насколько убедительно ЭТА позиция подтверждает совпадение по сигналам. */
const chargeSignalStrength = (c) => (c.matchedBy?.length ?? 0) * 2 + (c.dateMatch === 'exact' ? 1 : 0);
/**
 * Самая доказательная позиция человека — сначала по уверенности апстрима,
 * потом по числу совпавших сигналов; при полном равенстве побеждает первая
 * (выдача идёт DESC по дате, то есть первая — свежайшая).
 */
const strongestCharge = (charges) => charges.reduce((acc, c) => {
    const byConfidence = confidenceRank(c.confidence) - confidenceRank(acc.confidence);
    if (byConfidence !== 0)
        return byConfidence > 0 ? c : acc;
    return chargeSignalStrength(c) > chargeSignalStrength(acc) ? c : acc;
});
/**
 * Схлопывает списания в людей.
 *
 * Ключевой момент всего адаптера: `total: 3` сплошь и рядом означает ТРИ
 * СПИСАНИЯ ОДНОГО человека — подписка списывается ежемесячно с той же карты.
 * Считать такие строки тремя кандидатами значит объявить `ambiguous` там, где
 * сомнений нет, и отправить оператора разбирать пустоту. Ровно на этом стоит
 * путь `single-customer` ниже.
 *
 * Ключ схлопывания — лучший доступный идентификатор: наш `internalUserId`,
 * иначе покупатель Stripe, иначе почта, иначе сама позиция. Списания без
 * `internalUserId` остаются кандидатами (оператору есть что посмотреть), но
 * права на авто-привязку не дают — она возможна только по нашему id.
 *
 * Ступени при этом НЕ независимы, и на боевых данных это видно: у одной
 * позиции апстрим заполнил `internalUserId`, у соседней по тому же покупателю
 * Stripe — нет (`internalUserId` берётся из нашего зеркала, и на части
 * списаний он просто не проставлен). По прямой ступенчатой схеме такие строки
 * получают разные ключи (`user:` и `stripe:`) и становятся ДВУМЯ кандидатами —
 * искусственная неоднозначность на пути, который отвечает за бóльшую часть
 * ручной очереди. Поэтому покупатель Stripe работает ещё и СВЯЗЬЮ между
 * группами: позиция подсаживается в группу, уже занятую этим покупателем.
 *
 * Связь не срабатывает, когда у группы и у позиции РАЗНЫЕ наши id: один
 * покупатель Stripe на двух наших пользователей — это настоящая
 * неоднозначность, и склеить их значило бы привязать кейс к первому попавшемуся.
 */
const groupByCustomer = (items) => {
    const groups = new Map();
    /** Покупатель Stripe → ключ группы, в которой он уже встретился. */
    const groupByStripeCustomer = new Map();
    items.forEach((item, idx) => {
        const witUserId = clean(item.internalUserId);
        const email = firstNonEmpty(item.userEmail, item.stripeEmail, item.billingEmail);
        const chargeId = clean(item.chargeId);
        const stripeCustomerId = clean(item.stripeCustomerId);
        // Последняя ступень — ДВЕ разные ступени, а не одна с `?? idx`: позиция без
        // `chargeId` на индексе 0 и позиция с `chargeId: '0'` получили бы один и тот
        // же ключ `charge:0` и слиплись бы в одного кандидата. Решения о ПРИВЯЗКЕ
        // это не касается (у обеих нет нашего id, значит `single-customer`
        // невозможен), но занижает `groups.length` — то самое число кандидатов, по
        // которому оператор судит, много ли ему разбирать. Ровно поэтому `chargeId`
        // назван в докблоке `lenient` промежуточным случаем.
        const ownKey = witUserId != null
            ? `user:${witUserId}`
            : stripeCustomerId != null
                ? `stripe:${stripeCustomerId}`
                : email != null
                    ? `email:${email.toLowerCase()}`
                    : chargeId != null
                        ? `charge:${chargeId}`
                        : `row:${idx}`;
        const linked = stripeCustomerId != null ? groupByStripeCustomer.get(stripeCustomerId) : null;
        const linkedGroup = linked != null ? groups.get(linked) : null;
        const key = linked != null &&
            linkedGroup != null &&
            (witUserId == null || linkedGroup.witUserId == null || linkedGroup.witUserId === witUserId)
            ? linked
            : ownKey;
        if (stripeCustomerId != null && !groupByStripeCustomer.has(stripeCustomerId)) {
            groupByStripeCustomer.set(stripeCustomerId, key);
        }
        const existing = groups.get(key);
        if (existing == null) {
            groups.set(key, {
                witUserId,
                email,
                displayName: clean(item.billingName),
                // Эндпоинт сверяет `last4` и с картой, и с номером устройства кошелька —
                // при оплате Apple/Google Pay реальные цифры лежат в `walletLast4`.
                cardLast4: firstNonEmpty(item.card?.last4, item.card?.walletLast4),
                signals: new Set(item.matchedBy ?? []),
                charges: [item],
            });
            return;
        }
        for (const s of item.matchedBy ?? [])
            existing.signals.add(s);
        // Наш id дозаполняется наравне с остальными реквизитами: группа могла быть
        // заведена по покупателю Stripe с ещё неизвестным `internalUserId`, и без
        // этой строки человек остался бы «без нашего id» — то есть непривязываемым,
        // хотя id приехал соседней позицией. Именно `??=`, а не присваивание:
        // первый увиденный id остаётся за группой, а конфликтующий сюда и не
        // доходит — связь по покупателю Stripe его не пускает (см. выше).
        existing.witUserId ??= witUserId;
        existing.email ??= email;
        existing.displayName ??= clean(item.billingName);
        existing.cardLast4 ??= firstNonEmpty(item.card?.last4, item.card?.walletLast4);
        existing.charges.push(item);
    });
    return [...groups.values()];
};
const toAmountMatch = (m) => m == null
    ? null
    : { kind: m.kind, impliedRate: m.impliedRate ?? null, markupPct: m.markupPct ?? null };
/**
 * Одно списание в том виде, в каком его увидит оператор.
 *
 * Всё, что апстрим прислал по позиции, доезжает до карточки: выбор кандидата —
 * ручной, и делается он по датам, суммам, диспутам, объяснению расхождения
 * суммы и сетевому токену. Ничего из этого мы не «упрощаем» по дороге —
 * гадать оператор не должен.
 */
const toCandidateCharge = (c) => ({
    chargeId: clean(c.chargeId),
    paymentIntentId: clean(c.paymentIntentId),
    invoiceId: clean(c.invoiceId),
    createdAt: clean(c.createdAt),
    status: clean(c.status),
    amountCents: c.amount ?? null,
    currency: clean(c.currency)?.toUpperCase() ?? null,
    disputed: c.disputed ?? null,
    refunded: c.refunded ?? null,
    amountRefundedCents: c.amountRefunded ?? null,
    cardBrand: clean(c.card?.brand),
    cardFunding: clean(c.card?.funding),
    cardCountry: clean(c.card?.country),
    walletType: clean(c.card?.walletType),
    // ДВЕ РАЗНЫЕ пары цифр, и в показе они не схлопываются: `last4` — номер,
    // напечатанный на пластике и видимый в выписке, `walletLast4` — номер
    // устройства Apple/Google Pay. Алерт может нести любой из двух, и на живой
    // паре с прода оба кандидата показывали `7703` при номерах устройств `5439`
    // и `4548` — оператор, сверяющий алерт с номером устройства, отверг бы
    // верного кандидата. `firstNonEmpty` здесь оставлен только как запасной
    // вариант: если карточных цифр нет вовсе, лучше показать хоть какие-то, чем
    // пустое место.
    cardLast4: firstNonEmpty(c.card?.last4, c.card?.walletLast4),
    walletLast4: clean(c.card?.walletLast4),
    matchedBy: orderSignals(c.matchedBy ?? []),
    dateMatch: c.dateMatch ?? null,
    confidence: c.confidence,
    authCodeMatch: c.authCodeMatch ?? null,
    amountMatch: toAmountMatch(c.amountMatch),
    networkTokenUsed: c.card?.networkTokenUsed ?? null,
    networkTransactionId: clean(c.card?.networkTransactionId),
});
const toCandidate = (g) => ({
    witUserId: g.witUserId,
    email: g.email,
    displayName: g.displayName,
    cardLast4: g.cardLast4,
    matchedBy: orderSignals(g.signals),
    // Реквизиты человека собираются по всем его списаниям: апстрим заполняет их
    // неравномерно, и `stripeEmail` может стоять только на одной позиции из трёх.
    stripeCustomerId: firstNonEmpty(...g.charges.map((c) => c.stripeCustomerId)),
    stripeEmail: firstNonEmpty(...g.charges.map((c) => c.stripeEmail)),
    billingEmail: firstNonEmpty(...g.charges.map((c) => c.billingEmail)),
    billingName: firstNonEmpty(...g.charges.map((c) => c.billingName)),
    charges: g.charges.map(toCandidateCharge),
    // Уверенность человека — СИЛЬНЕЙШАЯ среди его списаний: кандидат стоит
    // столько, сколько его лучшее совпадение, и свернуть три ежемесячных
    // списания подписки в самое слабое значит спрятать ровно то, о котором алерт.
    confidence: strongestConfidence(g.charges),
});
/**
 * Человекочитаемое основание привязки. Первым сегментом ВСЕГДА идёт ПУТЬ —
 * `network-id`, `code+card`, `code+token` или наш `single-customer`: через
 * годы, когда калибровочные числа за каждым из них давно уедут, аудит должен
 * отвечать на вопрос «почему это привязали без человека» одним словом.
 *
 * Признаки берутся по ОДНОЙ позиции (тому списанию, которым подтвердились
 * якоря), а не по объединению: объединение могло бы утверждать
 * «authCode+last4» там, где ни одно списание не совпало сразу по обоим, а в
 * аудите основание должно описывать реальное списание.
 *
 * Восстановленный ведущий нуль называется прямо (`authCode(zero-padded)`) —
 * без этого «26044 против 026044» читается как разные коды. Расхождение суммы
 * тоже проговаривается: если её спрашивали, а апстрим не подтвердил, оператор
 * обязан понимать, что сумма в привязке не участвовала.
 */
const describeSignals = (best, askedSignals) => {
    // Пустой `matchedBy` — апстрим не прислал разбивку; тогда честнее назвать то,
    // что мы спрашивали, чем оставить основание пустым.
    const matched = best.matchedBy?.length ? best.matchedBy : askedSignals;
    const text = orderSignals(matched)
        .map((s) => {
        if (s === 'date' && best.dateMatch != null)
            return `date(${best.dateMatch})`;
        if (s === 'authCode' && best.authCodeMatch === 'zero-padded')
            return 'authCode(zero-padded)';
        return s;
    })
        .join('+');
    if (!askedSignals.includes('amount') || matched.includes('amount'))
        return text;
    // Сумму спрашивали, а совпадением она не стала. Когда апстрим объяснил
    // причину — называем её: «fee-markup» и «fx-plausible» означают ту же самую
    // сумму, просто в других деньгах, и молчание об этом выглядело бы как
    // необъяснённое расхождение.
    const why = best.amountMatch != null ? `сумма: ${best.amountMatch.kind}` : 'сумма не сошлась';
    return `${text} (${why})`;
};
const describeBasis = (args) => [
    args.path,
    describeSignals(args.best, args.askedSignals),
    ...(args.confidence != null ? [`уверенность ${args.confidence}`] : []),
    args.tail,
].join('; ');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const createHttpRiskAlertMatcher = (opts) => {
    const client = createServiceHttpClient({
        baseUrl: opts.baseUrl,
        acceptJson: true,
        ...(opts.apiKey !== undefined && { apiKey: opts.apiKey }),
        ...(opts.fetchFn !== undefined && { fetchFn: opts.fetchFn }),
        ...(opts.timeoutMs !== undefined && { timeoutMs: opts.timeoutMs }),
    });
    const logger = opts.logger ?? noopLogger;
    return {
        async match(q) {
            const criteria = buildCriteria(q);
            // Искать нечем — запроса не делаем вовсе. Бэкфилл всей истории породит
            // тысячи таких алертов (charge-поля пустые), и каждый из них апстрим
            // отверг бы 400-ым `SEARCH_CRITERIA_REQUIRED`.
            if (criteria == null)
                return { status: 'unavailable', reason: 'empty_query' };
            const res = await client.get(`${SEARCH_PATH}?${criteria.params.toString()}`);
            // Эндпоинта ещё нет — это НЕ ошибка и НЕ «не нашли». «Не нашли» апстрим
            // отдаёт 200-ым с пустым `items`, поэтому 404 однозначно означает
            // «роут не выкачен»; 405/501 — то же самое от шлюза и заглушки.
            if (res.status === 404 || res.status === 405 || res.status === 501) {
                return { status: 'unavailable', reason: 'endpoint_not_deployed' };
            }
            // Лимит апстрима — свой класс ошибки с разобранным Retry-After. Свалить
            // его в общий `throw` ниже значит потерять единственную подсказку о том,
            // когда повторять.
            if (res.status === 429) {
                throw new RiskMatcherRateLimitedError(parseRetryAfter(res.headers.get('retry-after')));
            }
            // 400 — апстрим отказался от ЭТОГО запроса; ретраить бессмысленно.
            // Два КЛАССА кодов, и каждому — свой исход.
            //
            // `SEARCH_CRITERIA_REQUIRED` — «нет ни одного критерия»: значит то же,
            // что наша проверка выше, и должен читаться как `empty_query`, а не как
            // сбой.
            //
            // ДЕТЕРМИНИРОВАННЫЕ коды валидации (`DETERMINISTIC_400_CODES` ниже) —
            // апстрим отверг конкретное ЗНАЧЕНИЕ по правилу собственного контракта
            // (`AUTH_CODE_TOO_SHORT`, `INVALID_NETWORK_ID`, `INVALID_AUTH_CODE`,
            // `INVALID_LAST4`, `INVALID_DATE`, `INVALID_AMOUNT`, `INVALID_CURRENCY`,
            // `CURRENCY_WITHOUT_AMOUNT`, `INVALID_SEARCH_PARAM`). Это ТОЖЕ дефект
            // нашего маппинга — как и любой другой 400 ниже, — но не такой, из-за
            // которого стоит ронять кейс ретраем BullMQ: одно и то же значение либо
            // ВСЕГДА проходит эту проверку, либо НИКОГДА, повтор не изменит исход.
            // Терминальный `unavailable/invalid_query`, а не `throw`: кейс уезжает
            // оператору как обычно (та же логика, что у `empty_query`), а код —
            // `logger.error`, чтобы дефект маппинга был виден без ретраев в петле.
            //
            // НЕЗНАКОМЫЙ код — по-прежнему `throw`. Новый код здесь означает, что
            // контракт апстрима УЖЕ УЕХАЛ ДАЛЬШЕ этого списка, и молча тушить его в
            // `unavailable` значило бы прятать дрейф контракта за штатным исходом;
            // пусть шумит ретраями, пока список не подтянут явной правкой.
            if (res.status === 400) {
                const body = await res.json().catch(() => null);
                const code = SearchErrorWire.safeParse(body).data?.code ?? null;
                if (code === 'SEARCH_CRITERIA_REQUIRED') {
                    return { status: 'unavailable', reason: 'empty_query' };
                }
                if (code != null && DETERMINISTIC_400_CODES.has(code)) {
                    logger.error('risk-matcher: HTTP 400 (детерминированный код валидации)', { code });
                    return { status: 'unavailable', reason: 'invalid_query' };
                }
                throw new Error(`risk-matcher: HTTP 400 (${code ?? 'без кода'})`);
            }
            // 401/403 отдаёт middleware, и форма тела не гарантирована — решение
            // принимаем по СТАТУСУ. Бросаем: неверный ключ надо чинить, а не
            // записывать кейсу терминальное «не нашли».
            if (res.status === 401 || res.status === 403) {
                throw new Error(`risk-matcher: HTTP ${res.status} — сервисный ключ отвергнут`);
            }
            // Всё остальное «не ok» — настоящий сбой: бросаем, чтобы BullMQ ретраил
            // (см. хендлер). Поиск — чтение, повтор безопасен. Проглотить сбой как
            // `unavailable` значит записать в кейс состояние по факту временной
            // пятисотки.
            if (!res.ok)
                throw new Error(`risk-matcher: HTTP ${res.status}`);
            const data = SearchEnvelope.parse(await res.json()).data;
            // «Никого не нашли» — это 200 с пустым `items`, и означает оно ровно
            // `not_found`: искали, в биллинге таких списаний нет.
            if (data.items.length === 0)
                return { status: 'not_found' };
            // Разбор по позициям терпимый: одна нечитаемая строка не должна
            // обесценивать всю выдачу. Но её потеря УЧИТЫВАЕТСЯ ниже — выброшенная
            // позиция могла принадлежать другому человеку, и тогда «все кандидаты —
            // один человек» посчитано по неполным данным.
            const parsed = data.items.flatMap((raw) => {
                const r = ChargeWire.safeParse(raw);
                return r.success ? [r.data] : [];
            });
            // Не разобралась НИ ОДНА позиция при непустом `items` — это не «ничего не
            // нашли», а разъехавшийся контракт. Молча отдать пустой список кандидатов
            // значит показать оператору пустой экран без единого намёка на причину.
            if (parsed.length === 0) {
                throw new Error(`risk-matcher: ни одна из ${data.items.length} позиций не разобрана`);
            }
            const dropped = data.items.length - parsed.length;
            const charges = parsed.map((c) => ({
                ...c,
                confidence: resolveConfidence(c, data.confidence ?? null),
            }));
            const groups = groupByCustomer(charges);
            // ЭФФЕКТИВНЫЙ ИСХОД. Значений у него пять вместе с `null`, но РАЗВИЛОК
            // всего две: `auto` открывает путь 1, `review` — путь 2, а `none`, `null`
            // и `contract-mismatch` поведенчески неотличимы — все трое ведут в
            // `ambiguous`. Отдельным значением расхождение сделано не ради ветвления,
            // а ради НАБЛЮДАЕМОСТИ: только так наружу уезжает `reason` (см. конец
            // функции), иначе разъехавшийся контракт неотличим от честной
            // неоднозначности.
            //
            // `autoLinkSafe` объявлен строгим синонимом `outcome === 'auto'`, поэтому
            // читаются ОБА и требуется согласие. Расхождение означает ровно одно:
            // контракт разъехался, и что именно сервер имел в виду — неизвестно.
            // Поэтому оно получает СВОЁ значение `contract-mismatch`, которое гасит
            // ОБА пути привязки и ведёт прямо в `ambiguous`.
            //
            // Свалить расхождение в `review` (как было раньше) нельзя, и это не
            // вопрос вкуса: `review` НЕ нейтрально — это ПРЕДУСЛОВИЕ пути 2
            // (`single-customer`). То есть «понижение до `review`» не блокировало
            // привязку, а перекладывало её с серверного решения на наше правило, и
            // давало два неверных исхода сразу. Первый: `outcome: 'none'` при
            // `autoLinkSafe: true` — сервер сказал «ничего не сходится», а мы
            // привязывали человека. Второй: `outcome: 'auto'` при пропавшем
            // `autoLinkSafe` (поле убрали как устаревшее — ровно тот случай, ради
            // которого читаются оба) — привязка происходила, но в аудит уезжал
            // `single-customer` вместо серверного пути, то есть решению сервера
            // приписывалось наше правило. Второй сценарий касается ВСЕХ автопривязок:
            // на боевой сверке это 1592 кейса, по любому из трёх путей.
            //
            // Направление гашения единственно возможное: цена ложной авто-привязки —
            // чужой биллинг в карточке клиента, цена лишнего разбора — один взгляд
            // оператора.
            const autoDeclared = data.outcome === 'auto';
            const autoSafe = data.autoLinkSafe === true;
            // `let`, а не `const`: ниже, в ветке `auto`, есть ОДИН легальный повод
            // переписать значение — контрактное нарушение вокруг `matchedChargeId`,
            // обнаруживаемое только внутри самой ветки (см. довод там). Остальной
            // файл по-прежнему читает `verdict` как решённое один раз значение.
            let verdict = autoDeclared !== autoSafe ? 'contract-mismatch' : (data.outcome ?? null);
            // ПУТЬ 1 — привязка по решению сервера.
            //
            // При `auto` контракт гарантирует РОВНО ОДНУ позицию, у которой
            // `internalUserId` и `userEmail` заполнены (сервер сам понижает ответ до
            // `review`, если пользователь неразрешим). Поэтому нескольких позиций
            // здесь быть не может, а если они пришли — контракт разъехался, и
            // выбирать одну из них по своему разумению значит вернуть ту самую
            // самодельную ранжировку, ради устранения которой контракт и переписан.
            //
            // Полноту выдачи на этот путь НЕ навешиваем: сервер считал по полному
            // множеству кандидатов, которое видит только он, а `truncated`/`total`
            // описывают лишь нашу страницу. Проверка «а всё ли мы увидели» спорила
            // бы с решением, принятым по большему знанию, и проигрывала бы ему.
            const single = data.items.length === 1 && charges.length === 1 ? charges[0] : null;
            if (verdict === 'auto' && single != null && clean(single.internalUserId) != null) {
                const autoLinkBasis = (data.autoLinkBasis ?? null);
                // Сервер сам назвал конкретное списание — доступно ТОЛЬКО на этом
                // пути (см. `RiskAlertMatchResult.matchedChargeId`): `single-customer`
                // ниже привязывает человека, а не списание, и выбора между
                // несколькими списаниями там нет.
                //
                // ЧЕРЕЗ `clean()`, а не голый `?? null` — тот же приём, что и у
                // `witUserId` выше, но с другим последствием, и его стоит назвать
                // точно. У `matchedChargeId` в сторе ТРЁХЗНАЧНАЯ семантика (см.
                // `writeIdentification` в `risk-alert-store.ts`): `undefined` — «эта
                // ось меня не касается», не-клобер; `null` — ЯВНАЯ очистка колонки;
                // непустая строка — установить. Не-клобер обеспечивает ВЫЗЫВАЮЩИЙ,
                // передавая `result.matchedChargeId ?? undefined`
                // (`risk-alert-identifier.ts`) — то есть решение «не трогать» живёт
                // на один слой выше, а наше дело — не выдать `""` за значение.
                //
                // ФОЛБЭК на `single.chargeId` — контракт 2026-08-27: спека гарантирует
                // `data.matchedChargeId === items[0].chargeId` на пути `auto` (см.
                // шапку `payment-charge-search-openapi.yaml`), то есть оба значения —
                // ОДНО И ТО ЖЕ по контракту, а не два разных источника истины. Фолбэк
                // — страховка переходного периода: старый апстрим без поля конверта
                // или конверт с испорченным типом (`clean` даёт `null`) не должны
                // возвращать кейс к «списание неизвестно», когда оно доступно вторым
                // путём. На пути `single-customer` этот фолбэк не сработает никогда —
                // там `verdict !== 'auto'`, а решение принципиально не про одно
                // списание.
                const matchedChargeId = clean(data.matchedChargeId) ?? clean(single.chargeId);
                if (matchedChargeId != null) {
                    return {
                        status: 'matched',
                        witUserId: clean(single.internalUserId),
                        confidence: single.confidence,
                        autoLinkBasis,
                        matchedChargeId,
                        basis: describeBasis({
                            // Сервер вправе не назвать путь; тогда честнее сказать «auto»,
                            // чем выдумать один из четырёх калиброванных.
                            path: autoLinkBasis ?? 'auto',
                            best: single,
                            askedSignals: criteria.askedSignals,
                            confidence: single.confidence,
                            tail: `из ${plural(groups.length, 'кандидата', 'кандидатов')}`,
                        }),
                    };
                }
                // НИ конверт, НИ единственная позиция не назвали списание — контракт
                // 2026-08-27 гарантирует, что это невозможно на пути `auto`, значит
                // это ТО ЖЕ САМОЕ контрактное нарушение, что рассогласование
                // `outcome`/`autoLinkSafe` выше (см. довод там), только обнаруженное
                // позже, внутри самой ветки. Привязывать клиента к списанию, id
                // которого мы не знаем, нельзя: `matchedChargeId` таргетирует
                // РЕАЛЬНЫЙ возврат денег (см. payment-order-2), а не просто витрину.
                // Падаем в `ambiguous` с `reason: 'contract_mismatch'` через общий
                // хвост функции — `verdict` меняется здесь единственный раз во всём
                // файле.
                verdict = 'contract-mismatch';
            }
            // ПОЛНОТА ВЫДАЧИ для пути 2 — четыре условия, и `truncated` среди них
            // намеренно НЕТ.
            //
            // РЕЖУТ ДВА РАЗНЫХ НОЖА, и путать их нельзя. Предохранитель просмотра у
            // СЕРВЕРА стоит на КАЖДЫЙ ПРИЗНАК отдельно (2 000 кандидатов), а не на
            // запрос целиком, и это не деталь реализации: иначе общий лимит тратился
            // бы «сначала самые свежие» на все признаки сразу, и широкая ветка даты
            // вытесняла бы более старое списание, найденное по коду авторизации.
            // Селективные ветки до этого предела сегодня не доходят — код даёт
            // максимум 5 списаний, карта 1 073. Второй нож — НАШ `limit`, он режет
            // отданную страницу, и от него защищает пункт 2 ниже.
            //
            // `truncated: true` относится к ПЕРВОМУ ножу и потому в условия полноты
            // не входит: он говорит «какая-то ветка упёрлась в 2 000», почти всегда
            // суточная, и ничего не говорит про наш `limit`. Утверждать «значит ответ
            // не обрезан» было бы неправдой — в боевом кейсе №2863 первый уровень дал
            // 1 948 кандидатов при странице в 100 строк, то есть ответ обрезан и
            // нашим лимитом тоже; просто ловит это пункт 2, а не флаг.
            //
            // ЭТО ДОПУЩЕНИЕ С ЗАПАСОМ, А НЕ ВЕЧНОЕ СВОЙСТВО, и здесь записано, чем
            // именно оно держится. Замер 25.08.2026 прямыми запросами к боевому
            // сервису по одному только `last4` (с `includeNonSucceeded`): 66, 33, 6,
            // 148, 84 кандидата — при пределе в 2 000 и наблюдавшемся максимуме по
            // всему зеркалу 1 073. Запас примерно двукратный ДАЖЕ на самой населённой
            // четвёрке цифр.
            //
            // Что его отменит: рост зеркала настолько, что ветка карты сама упрётся в
            // 2 000. Тогда за пределами просмотренного может оказаться списание
            // ДРУГОГО клиента того же уровня, `single-customer` привяжет не того, и
            // отличить этот случай по ответу будет нечем — `truncated` один на весь
            // ответ и не говорит, какая ветка обрезана. Признак близости к границе
            // тоже недоступен: `levelCounts['1']` считает и ветку даты, которая на
            // порядок шире (в боевом кейсе №2863 — 1 948 против 66 у карты), и
            // разложить его обратно нельзя. Поэтому проверять надо тем же прямым
            // замером по одному `last4`, а не полагаться на поле ответа. Замер: из 49
            // боевых кейсов, где выдача схлопывается в одного известного клиента, у 43
            // стоит `truncated: true` — запрет по нему выбросил бы 43 привязки из 49
            // по причине, которая к ответу не относится. Сервер и сам так не считает:
            // боевой кейс №2863 приехал с `truncated: true` и `outcome: auto`
            // одновременно.
            //
            // Вместо него:
            //  1. `dropped === 0` — выброшенная при разборе позиция могла
            //     принадлежать другому человеку, и тогда «один клиент» посчитано по
            //     неполным данным. Это условие НАШЕ, апстрим о нём не знает;
            //  2. `total` ПРИШЁЛ и не больше отданной страницы — лучший уровень отдан
            //     целиком, а не обрезан нашим `limit`. Требуется именно приход поля, а
            //     не «пришёл и сошёлся»: `total` — единственная защита от обрезания
            //     нашим `limit` (`truncated` мы намеренно не читаем, `dropped` ловит
            //     только сбой разбора, а условие 4 смотрит лишь на ПОЛУЧЕННЫЕ
            //     позиции), и «поля нет ⇒ считаем полной» снимало бы её целиком. Цена
            //     ошибки известна поимённо: ветка карты у апстрима даёт до 1 073
            //     списаний, выдача отсортирована по дате DESC, и тяжёлый подписчик
            //     занимает всю первую сотню — списание другого клиента с теми же
            //     четырьмя цифрами, то самое, о котором алерт, лежит на 130-й позиции
            //     и в страницу не попадает. Среди 49 боевых кейсов случаев
            //     `total > items.length` нет ни одного, то есть строгость не стоит нам
            //     ни одной привязки;
            //  3. `items.length < SEARCH_LIMIT` — второй пояс к пункту 2, на случай
            //     если `total` соврал или считается сервером по другому множеству.
            //     Страница, забитая ровно под лимит, подозрительна сама по себе:
            //     полная выдача, случайно совпавшая с лимитом ровно, неотличима от
            //     обрезанной. Замер ИМЕННО ПО ЭТОМУ ПУНКТУ (отдельный от замера про
            //     `total` выше): полный прогон ужесточённых условий по всем 230 боевым
            //     ответам дал те же 142 `auto` + 49 `single-customer` + 39 оператору —
            //     ни одна из 49 привязок этим поясом не отсекается, у них в выдаче 1,
            //     2 или 3 позиции, до сотни очень далеко. У оставшихся 39 единственная
            //     причина отсева — кандидаты у разных людей;
            //  4. у КАЖДОЙ позиции в `matchedBy` есть селективный якорь. Уровень,
            //     собранный из одной даты (и, возможно, суммы), полнотой не обладает
            //     ни при каком `truncated`: там предохранитель как раз и срабатывает,
            //     и «один клиент» означало бы лишь «в просмотренном куске суток
            //     попался один человек».
            const complete = dropped === 0 &&
                data.total != null &&
                data.total <= data.items.length &&
                data.items.length < SEARCH_LIMIT &&
                charges.every((c) => (c.matchedBy ?? []).some((s) => SELECTIVE_SIGNALS.includes(s)));
            const onlyGroup = groups.length === 1 ? groups[0] : null;
            // УРОВНЕВАЯ уверенность ответа — то, что проверяет порог ниже, и то, что
            // уезжает в результат как уверенность привязки. НЕ максимум среди
            // списаний: `resolveConfidence` размножает уровневую уверенность на
            // позиции без своей, группа на этом пути ровно одна, и «сильнейшая среди
            // списаний» схлопнулась бы в максимум по всему ответу — то есть порог
            // перестал бы быть независимой проверкой. Хуже: одно сильное списание
            // поднимало бы над порогом группу, где все остальные слабые, причём
            // сильное может не иметь к алерту никакого отношения.
            const envelopeConfidence = data.confidence ?? null;
            // ПУТЬ 2 — `single-customer`, единственное решение, оставшееся НАШИМ.
            //
            // Сервер ответил `review`, потому что не смог выбрать, КАКОЕ ИМЕННО
            // списание имел в виду алерт: три ежемесячных списания подписки, серия
            // повторных попыток dunning'а. Документация так этот случай и называет —
            // «Пользователь найден, но какое именно списание?».
            //
            // Для нас вопрос не стоит: кейс привязывает ЧЕЛОВЕКА, а не списание.
            // Если вся выдача схлопнулась в одного человека с нашим id, то какое бы
            // списание ни имелось в виду, клиент один и тот же. Именно поэтому путь
            // называется отдельно, а не выдаётся за серверный: калибровки апстрима
            // за ним нет, за ним стоит другое утверждение.
            //
            // Путь не редкий: прогон 25.08.2026 по 230 неразобранным кейсам прода дал
            // 88 ответов `review`, и 49 из них схлопываются ровно в одного известного
            // клиента — то есть 49 кейсов ручной очереди из 230 разбирает это правило
            // и только оно (остальные 142 закрывает серверный `auto`). Ошибка в
            // правиле стоит чужого биллинга в карточке, умноженного на 49. Отсюда два
            // ограничителя ниже — порог уверенности и полнота.
            //
            // ПОРОГ `medium` взят из СЕРВЕРНОЙ калибровки, а не выдуман нами:
            // документация определяет `medium` как «карта + дата + сумма, когда ВСЕ
            // кандидаты лучшего уровня принадлежат одному клиенту (его собственные
            // dunning-попытки — не неоднозначность)» — дословно наш случай, уже
            // посчитанный сервером. `low` — «слабее, в том числе голый код
            // авторизации», а голого кода мало: 11–40% случайных кодов существуют в
            // зеркале платёжного сервиса безо всякой связи с алертом. Один кандидат
            // при поиске только по дате или только по коду — это не «пользователь
            // найден», а «на эти сутки нашёлся один человек».
            //
            // Порог и наш подсчёт «схлопнулось ровно в одного» — ДВЕ проверки одного
            // факта: одна сервером, другая нами. Держать обе правильно, и проверять
            // порог надо ИМЕННО уровневой уверенностью ответа: её сервер откалибровал,
            // и её определение («все кандидаты лучшего уровня — один клиент») делает
            // порог осмысленным. Замер это подтверждает: из 88 боевых `review` в
            // одного известного клиента схлопнулись 49, и у всех 49
            // `confidence: medium` — ни одного `low`. Отсутствующая уверенность
            // порогом НЕ считается пройденной: право на привязку подтверждается, а не
            // предполагается.
            if (verdict === 'review' &&
                complete &&
                onlyGroup != null &&
                onlyGroup.witUserId != null &&
                confidenceRank(envelopeConfidence) >= confidenceRank(MIN_SINGLE_CUSTOMER_CONFIDENCE)) {
                return {
                    status: 'matched',
                    witUserId: onlyGroup.witUserId,
                    confidence: envelopeConfidence,
                    autoLinkBasis: 'single-customer',
                    basis: describeBasis({
                        path: 'single-customer',
                        // Показать оператору лучшее списание — по-прежнему максимум:
                        // объяснение привязки должно называть самое доказательное списание
                        // человека. Порогом это не является — им является уверенность
                        // ответа выше.
                        best: strongestCharge(onlyGroup.charges),
                        askedSignals: criteria.askedSignals,
                        confidence: envelopeConfidence,
                        tail: `из ${plural(onlyGroup.charges.length, 'списания', 'списаний')} одного клиента`,
                    }),
                };
            }
            return {
                status: 'ambiguous',
                candidates: groups.map(toCandidate),
                // Считаем ЛЮДЕЙ, а не списания: три ежемесячных списания одного
                // человека — это один кандидат, и «total: 3» в логе означал бы
                // несуществующую неоднозначность. При `truncated` это нижняя оценка.
                total: groups.length,
                // РАЗЪЕХАВШИЙСЯ КОНТРАКТ ВИДЕН СНАРУЖИ. Без этого поля гашение обоих
                // путей неотличимо от честной неоднозначности: наружу уходит обычный
                // `ambiguous`, и событие, ради ОБНАРУЖЕНИЯ которого механизм и заведён,
                // в проде обнаружить нечем. Молчание здесь — та же ошибка, из-за
                // которой «не разобралась ни одна из N позиций» выше честно бросает.
                //
                // Бросать тут нельзя: расхождение полей ответа — состояние УСТОЙЧИВОЕ,
                // повтор придёт таким же, и бросок дал бы бесконечный ретрай BullMQ
                // вместо разбора кейса человеком. Поэтому кейс уезжает оператору как
                // обычно, а причина — в лог потребителя.
                ...(verdict === 'contract-mismatch' && { reason: 'contract_mismatch' }),
            };
        },
    };
};
//# sourceMappingURL=matcher-http.js.map